/**
 * `<caos-object-field-explorer>` — an object's fields, filtered by type and origin, with how many records actually use each one.
 * (Catalog: Developer surfaces › ObjectFieldExplorer — an object's field list, filtered by type and origin, with real usage against each field.)
 *
 * USAGE IS A COLUMN, not something you hover for. "This field is populated on 62% of records" is the
 * question every person asks before deciding whether a field can be retired, and it is the one thing
 * neither the incumbent console nor the original specification had. Putting it behind a hover makes
 * it a thing you find rather than a thing you see, which for a number that answers the reason you
 * opened the screen is the same as not having it.
 *
 * A FIELD WITH NO USAGE FIGURE IS NOT A FIELD WITH ZERO USAGE. Usage has to be measured, measuring
 * costs something, and a surface may well arrive with figures for some fields and not others. Those
 * rows read "not measured" and are excluded from the "N never used" count — the same distinction
 * `<caos-coverage-list>` makes for an artifact with no executable lines, and for the same reason: a
 * screen that reports unmeasured things as empty ones teaches people to stop believing it.
 *
 * ORIGIN IS A FILTER, NOT A SEARCH TERM. "Show me the custom fields" is the most common thing
 * anybody does on this screen, and making it a text search over names both misses custom fields that
 * do not say so in their name and matches standard ones that do.
 *
 * The filters INTERSECT, and the summary always states how many of the whole set survived them — a
 * filtered list that does not say it is filtered is how somebody concludes an object has four fields.
 *
 * Styling seams: `::part(explorer)`, `::part(filters)`, `::part(filter)`, `::part(search)`,
 * `::part(summary)`, `::part(table)`, `::part(usage)`, `::part(export)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import './usage-bar.js';
import type { CaosDataTable, CaosSortDirection, CaosTableRow } from './data-table.js';

/** Where a field came from. */
export type CaosFieldOrigin = 'standard' | 'custom';

/** One field of the object. */
export interface CaosObjectField {
  readonly apiName: string;
  readonly label: string;
  /** The field's type, as a word a developer recognises — `Checkbox`, `Currency`, `Long Text Area`. */
  readonly type: string;
  readonly origin: CaosFieldOrigin;
  /** How many records have it populated. ABSENT ⇒ not measured, which is not the same as zero. */
  readonly populated?: number;
  /** How many records there are to be populated. Absent ⇒ no proportion can be stated. */
  readonly recordCount?: number;
}

const sheet = makeSheet(`
  :host { display: block; }

  .explorer { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .filters {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    flex-wrap: wrap;
  }

  .filters select, .filters input {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .filters input { flex: 1 1 180px; min-width: 150px; }
  .filters select:focus-visible, .filters input:focus-visible, .export:focus-visible {
    outline: none;
    box-shadow: var(--caos-focus-ring);
  }

  .export {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 10px;
    cursor: pointer;
  }
  .export:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }

  .summary {
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
`);

/**
 * What proportion of records have this field populated, or `undefined` when it was not measured.
 *
 * See the header: not measured and zero are different claims, and a list that collapses them
 * reports a field nobody counted as a field nobody uses.
 */
export function fieldUsagePercent(field: CaosObjectField): number | undefined {
  if (field.populated === undefined || !Number.isFinite(field.populated)) return undefined;
  const total = field.recordCount;
  if (total === undefined || !Number.isFinite(total) || total <= 0) return undefined;
  return Math.max(0, Math.min(100, (field.populated / total) * 100));
}

/** The distinct types present, in the order they first occur — the contents of the type filter. */
export function fieldTypesOf(fields: readonly CaosObjectField[]): readonly string[] {
  const seen: string[] = [];
  for (const f of fields) if (!seen.includes(f.type)) seen.push(f.type);
  return seen;
}

export class CaosObjectFieldExplorer extends CaosElement {
  static get observedAttributes(): string[] {
    return ['type', 'origin', 'search', 'record-count', 'sort-key', 'sort-direction'];
  }

  private typeEl: HTMLSelectElement;
  private originEl: HTMLSelectElement;
  private searchEl: HTMLInputElement;
  private exportEl: HTMLButtonElement;
  private summaryEl: HTMLParagraphElement;
  private tableEl: CaosDataTable;

  private data: readonly CaosObjectField[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'explorer';
    wrap.setAttribute('part', 'explorer');

    const filters = document.createElement('div');
    filters.className = 'filters';
    filters.setAttribute('part', 'filters');

    this.typeEl = this.select('Field type', 'type');
    this.originEl = this.select('Field origin', 'origin');
    this.searchEl = document.createElement('input');
    this.searchEl.type = 'search';
    this.searchEl.setAttribute('part', 'search');
    this.searchEl.placeholder = 'Search fields';
    this.searchEl.setAttribute('aria-label', 'Search fields by name or label');
    this.searchEl.addEventListener('input', () => this.setAttribute('search', this.searchEl.value));

    this.exportEl = document.createElement('button');
    this.exportEl.type = 'button';
    this.exportEl.className = 'export';
    this.exportEl.setAttribute('part', 'export');
    this.exportEl.textContent = 'Export';
    this.exportEl.addEventListener('click', () => this.exportFields());

    filters.append(this.typeEl, this.originEl, this.searchEl, this.exportEl);

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      e.stopPropagation();
      this.setAttribute('sort-key', column);
      this.setAttribute('sort-direction', direction);
    });

    wrap.append(filters, this.summaryEl, this.tableEl);
    this.root.append(wrap);
  }

  private select(label: string, attr: string): HTMLSelectElement {
    const s = document.createElement('select');
    s.setAttribute('part', 'filter');
    s.setAttribute('aria-label', label);
    s.addEventListener('change', () => {
      if (s.value === '') this.removeAttribute(attr);
      else this.setAttribute(attr, s.value);
      this.dispatchEvent(
        new CustomEvent('caos-field-filter', {
          bubbles: true,
          composed: true,
          detail: { type: this.getAttribute('type'), origin: this.getAttribute('origin') },
        }),
      );
    });
    return s;
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, _before: string | null, after: string | null): void {
    if (name === 'search' && this.searchEl.value !== (after ?? '')) this.searchEl.value = after ?? '';
    this.render();
  }

  get fields(): readonly CaosObjectField[] {
    return this.data;
  }

  set fields(next: readonly CaosObjectField[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  /** How many records the object holds, when the caller has not put it on each field. */
  private get recordCount(): number | undefined {
    const n = Number(this.getAttribute('record-count'));
    return this.hasAttribute('record-count') && Number.isFinite(n) ? n : undefined;
  }

  /** A field with the object-level record count filled in, so usage can be computed either way. */
  private resolved(field: CaosObjectField): CaosObjectField {
    return field.recordCount === undefined && this.recordCount !== undefined
      ? { ...field, recordCount: this.recordCount }
      : field;
  }

  /** The fields surviving every filter, in the current order. */
  get visibleFields(): readonly CaosObjectField[] {
    const type = this.getAttribute('type');
    const origin = this.getAttribute('origin');
    const term = (this.getAttribute('search') ?? '').trim().toLowerCase();
    const kept = this.data.filter((f) => {
      if (type !== null && type !== '' && f.type !== type) return false;
      if (origin !== null && origin !== '' && f.origin !== origin) return false;
      if (term !== '' && !f.apiName.toLowerCase().includes(term) && !f.label.toLowerCase().includes(term)) return false;
      return true;
    });
    return this.order(kept);
  }

  /** The fields measured as populated on no records at all. Unmeasured fields are NOT in here. */
  get neverUsed(): readonly CaosObjectField[] {
    return this.data.filter((f) => f.populated === 0);
  }

  private order(fields: readonly CaosObjectField[]): readonly CaosObjectField[] {
    const key = this.getAttribute('sort-key') ?? 'apiName';
    const sign = this.getAttribute('sort-direction') === 'desc' ? -1 : 1;
    return [...fields].sort((a, b) => {
      if (key === 'usage') {
        // Unmeasured rows sort to the END whichever way the column is pointing: they are not a
        // usage of zero, so they do not belong at the bottom of an ascending sort by usage either.
        const x = fieldUsagePercent(this.resolved(a));
        const y = fieldUsagePercent(this.resolved(b));
        if (x === undefined && y === undefined) return a.apiName.localeCompare(b.apiName);
        if (x === undefined) return 1;
        if (y === undefined) return -1;
        if (x !== y) return (x - y) * sign;
        return a.apiName.localeCompare(b.apiName);
      }
      const read = (f: CaosObjectField): string => String((f as unknown as Record<string, unknown>)[key] ?? '');
      const cmp = read(a).localeCompare(read(b), undefined, { numeric: true, sensitivity: 'base' });
      return (cmp !== 0 ? cmp : a.apiName.localeCompare(b.apiName)) * sign;
    });
  }

  private exportFields(): void {
    this.dispatchEvent(
      new CustomEvent('caos-field-export', { bubbles: true, composed: true, detail: { fields: this.visibleFields } }),
    );
  }

  private render(): void {
    this.renderFilters();

    this.tableEl.columns = [
      { key: 'apiName', label: 'API name', sortable: true, width: '26%' },
      { key: 'label', label: 'Label', sortable: true, width: '22%' },
      { key: 'type', label: 'Type', sortable: true, width: '16%' },
      { key: 'origin', label: 'Origin', sortable: true, width: '12%' },
      { key: 'usage', label: 'Usage', sortable: true },
    ];
    this.tableEl.sort = {
      key: this.getAttribute('sort-key') ?? 'apiName',
      direction: this.getAttribute('sort-direction') === 'desc' ? 'desc' : 'asc',
    };
    const visible = this.visibleFields;
    this.tableEl.rows = visible.map((f) => this.rowFor(this.resolved(f)));

    const nodes: Node[] = [];
    const strong = document.createElement('strong');
    strong.textContent =
      visible.length === this.data.length
        ? `${this.data.length} fields`
        : `${visible.length} of ${this.data.length} fields`;
    nodes.push(strong);
    const never = this.neverUsed.length;
    if (never > 0) nodes.push(document.createTextNode(` · ${never} populated on no records`));
    const unmeasured = this.data.filter((f) => fieldUsagePercent(this.resolved(f)) === undefined).length;
    if (unmeasured > 0) nodes.push(document.createTextNode(` · ${unmeasured} not measured`));
    this.summaryEl.replaceChildren(...nodes);

    this.exportEl.disabled = visible.length === 0;
  }

  private renderFilters(): void {
    const fill = (el: HTMLSelectElement, anyLabel: string, values: readonly string[], current: string | null): void => {
      const chosen = current ?? '';
      el.replaceChildren(
        ...[{ v: '', t: anyLabel }, ...values.map((v) => ({ v, t: v }))].map(({ v, t }) => {
          const o = document.createElement('option');
          o.value = v;
          o.textContent = t;
          o.selected = v === chosen;
          return o;
        }),
      );
    };
    // The type filter's contents are the types PRESENT, not a fixed list: an object has the types it
    // has, and offering a type nothing on this object uses is offering an empty result.
    fill(this.typeEl, 'Any type', fieldTypesOf(this.data), this.getAttribute('type'));
    fill(this.originEl, 'Any origin', ['standard', 'custom'], this.getAttribute('origin'));
  }

  private rowFor(field: CaosObjectField): CaosTableRow {
    const percent = fieldUsagePercent(field);
    const cell = document.createElement('span');
    cell.setAttribute('part', 'usage');

    if (percent === undefined) {
      cell.className = 'caos-table__cell-note';
      cell.textContent = 'not measured';
    } else {
      const bar = document.createElement('caos-usage-bar');
      bar.setAttribute('used', String(field.populated ?? 0));
      bar.setAttribute('maximum', String(field.recordCount ?? 0));
      bar.setAttribute('unit', 'records');
      // No threshold ramp. A field populated on 90% of records is not "nearly out" of anything —
      // the usage bar's consumption colours would read as an alarm about a healthy field.
      bar.setAttribute('intent', 'accent');
      bar.setAttribute('label', `${Math.round(percent)}%`);
      cell.append(bar);
    }

    return {
      id: field.apiName,
      cells: {
        apiName: field.apiName,
        label: field.label,
        type: field.type,
        origin: field.origin,
        usage: { node: cell },
      },
    } as CaosTableRow;
  }
}

define('caos-object-field-explorer', CaosObjectFieldExplorer);

declare global {
  interface HTMLElementTagNameMap {
    'caos-object-field-explorer': CaosObjectFieldExplorer;
  }
}
